import React, { useMemo } from 'react'
import { useParams } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { MeetingRoomForm } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/MeetingRoomForm'
import { IMeetingRoomFormData } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/types'
import { useSaveMeetingRoom } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/hooks/useSaveMeetingRoom'
import { showToast } from 'components/toast'
import { getHubRoute } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { trans } from 'sharedKernel/i18n'
import { GetFormat, useGet } from 'sharedKernel/api'
import { Loading } from 'components/loading'
import {
  MEETING_ROOM_API,
} from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/constants'
import {
  mapMeetingRoomToFormData,
  normalizeMeetingRoomDetailResponse,
} from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/mappers'

export const HubCompanyEditMeetingRoomPage = (): JSX.Element => {
  const { meetingRoomId } = useParams()
  const navigate = useNavigate()

  const request: GetFormat = {
    url: MEETING_ROOM_API.byId(meetingRoomId ?? ''),
  }

  const { data, isFetching, isError } = useGet<unknown>(
    request,
    `meeting-room-${meetingRoomId ?? ''}`,
    { enabled: !!meetingRoomId }
  )

  const room = useMemo(() => normalizeMeetingRoomDetailResponse(data), [data])

  const initialData = useMemo(
    () => (room ? mapMeetingRoomToFormData(room) : undefined),
    [room]
  )

  const { save, isLoading } = useSaveMeetingRoom()

  const handleSave = async (formData: IMeetingRoomFormData): Promise<void> => {
    if (!meetingRoomId) return
    await save(formData, meetingRoomId)
    showToast('success', trans('meetingRoom.toast.updateSuccess'))
    navigate(getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE))
  }

  if (isFetching) {
    return <Loading />
  }

  if (isError || !room || !initialData) {
    return (
      <div className="p-4 text-center text-muted">
        {trans('meetingRoom.edit.loadError')}
      </div>
    )
  }

  return (
    <MeetingRoomForm
      isEditing
      meetingRoomId={meetingRoomId}
      initialData={initialData}
      onSave={handleSave}
      isSaving={isLoading}
    />
  )
}
